import { UrlEventType, UrlParameter, VisitType } from 'src/requester';
import {
  IKameleoonData,
  KameleoonData,
  OperatingSystemDataType,
  OperatingSystemIndexMap,
  OperatingSystemType,
  OperatingSystemTypeMap,
} from './types';
import { TrackingStatus } from 'src/types';

/**
 * @class
 * OperatingSystem - a class for creating an instance for Operating System of a certain type
 * */
export class OperatingSystem implements IKameleoonData {
  private operatingSystem: OperatingSystemType;
  public status: TrackingStatus;

  /**
   * @param {OperatingSystemType} operatingSystem - one of the available operating system types from `OperatingSystemType` enum
   * */
  constructor(operatingSystem: OperatingSystemType) {
    this.operatingSystem = operatingSystem;
    this.status = TrackingStatus.Unsent;
  }

  get url(): string {
    // --- Note ---
    // If SDK is used in vanilla JS codebase, then you're also able to create an instance
    // with no required data, we don't want send anything to tracking in that case
    if (!this.operatingSystem) {
      return '';
    }

    return (
      UrlEventType.StaticData +
      UrlParameter.Os +
      this.operatingSystem +
      UrlParameter.OsIndex +
      OperatingSystemIndexMap[this.operatingSystem]
    );
  }

  get data(): OperatingSystemDataType {
    return {
      operatingSystem: this.operatingSystem,
      type: KameleoonData.OperatingSystem,
      status: this.status,
    };
  }

  /**
   * @private
   * @method _fromRaw - a static method for creating an instance of OperatingSystem class from raw data
   * @param {OperatingSystemDataType} data - a raw data
   * @returns {OperatingSystem} an instance of OperatingSystem class
   * */
  public static _fromRaw(data: OperatingSystemDataType): OperatingSystem {
    const { operatingSystem, status } = data;
    const os = new OperatingSystem(operatingSystem);

    os.status = status;

    return os;
  }

  /**
   * @private
   * @method _fromVisit - a static method for creating an instance of OperatingSystem class from a visit
   * @param {VisitType} visit - a visit
   * @returns {OperatingSystem | undefined} an instance of OperatingSystem class or undefined
   * */
  public static _fromVisit(visit: VisitType): OperatingSystem | undefined {
    if (!visit.staticDataEvent) {
      return undefined;
    }

    const { os: operatingSystemType, osIndex } = visit.staticDataEvent.data;

    if (operatingSystemType || typeof osIndex === 'number') {
      const operatingSystem =
        operatingSystemType ?? OperatingSystemTypeMap[osIndex as number];

      return new OperatingSystem(operatingSystem);
    }

    return undefined;
  }
}
